import { useEmployeeStats } from '@/hooks/use-employees';
import { useDepartments } from '@/hooks/use-organizations';
import {
  getCalendarEvents,
  type CalendarEvent,
} from '@/lib/absences';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { isEditableTarget } from '@/lib/keyboard';
import { useQuery } from '@tanstack/react-query';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { ScheduleGrid } from './schedule-grid';
import { ScheduleSummary } from './schedule-summary';
import { ScheduleToolbar } from './schedule-toolbar';
import { Skeleton } from '@/components/ui/skeleton';

export function ScheduleView() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBirthdayOnly, setShowBirthdayOnly] = useState(false);

  const formatName = useEmployeeNameFormatter();
  const { data: departments } = useDepartments();
  const { data: employeeStats } = useEmployeeStats();
  const totalActiveEmployees = (employeeStats?.active ?? 0) + (employeeStats?.probation ?? 0);

  const dateRange = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return {
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd'),
    };
  }, [currentDate]);

  const { data: calendarData, isLoading } = useQuery({
    queryKey: ['calendar-events', dateRange.start, dateRange.end, departmentFilter],
    queryFn: () =>
      getCalendarEvents({
        startDate: dateRange.start,
        endDate: dateRange.end,
        departmentId: departmentFilter !== 'all' ? departmentFilter : undefined,
      }),
  });

  const events = calendarData?.events ?? [];
  const absenceTypes = calendarData?.absenceTypes ?? [];

  // Group events by employee
  const employeesWithEvents = useMemo(() => {
    const map = new Map<
      string,
      { id: string; name: string; avatar: string | null; position: string | null; events: CalendarEvent[] }
    >();

    for (const event of events) {
      if (!event.employeeId) continue;
      const existing = map.get(event.employeeId);
      if (existing) {
        existing.events.push(event);
      } else {
        map.set(event.employeeId, {
          id: event.employeeId,
          name: formatName({
            firstName: event.employeeFirstName ?? '',
            lastName: event.employeeLastName ?? '',
            preferredName: event.employeePreferredName,
          }),
          avatar: event.employeeAvatar,
          position: event.departmentName,
          events: [event],
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [events, formatName]);

  // Filter by search query and birthday-only toggle
  const filteredEmployees = useMemo(() => {
    let result = employeesWithEvents;

    if (!showBirthdayOnly) {
      result = result.filter((e) =>
        e.events.some((ev) => ev.type !== 'birthday')
      );
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((e) => e.name.toLowerCase().includes(q));
    }

    return result;
  }, [employeesWithEvents, searchQuery, showBirthdayOnly]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (isEditableTarget(target)) {
        // Defer the blur if an inner handler (open dialog) already consumed Escape.
        if (e.key === 'Escape' && !e.defaultPrevented) target.blur();
        return;
      }

      if (e.key === 'ArrowLeft') {
        setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
      } else if (e.key === 't' || e.key === 'T') {
        setCurrentDate(new Date());
      } else if (e.key === '/') {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('[data-schedule-search]')?.focus();
      } else if (e.key === 'Escape' && !e.defaultPrevented) {
        // Defer to any inner handler that already consumed Escape (open dialogs).
        setSearchQuery('');
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-2 p-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ScheduleSummary events={events} totalEmployees={totalActiveEmployees} />
      <ScheduleToolbar
        currentDate={currentDate}
        onDateChange={setCurrentDate}
        departmentFilter={departmentFilter}
        onDepartmentChange={setDepartmentFilter}
        departments={departments ?? []}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        visibleCount={filteredEmployees.length}
        totalCount={employeesWithEvents.length}
        absenceTypes={absenceTypes}
        employees={filteredEmployees}
        showBirthdayOnly={showBirthdayOnly}
        onShowBirthdayOnlyChange={setShowBirthdayOnly}
      />
      <ScheduleGrid
        employees={filteredEmployees}
        currentDate={currentDate}
        searchQuery={searchQuery}
      />
    </div>
  );
}
